import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import PropertyCard from '../../components/FeaturedHomes/card';
import useAuth from '../../hooks/useAuth';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import LocationAutocomplete from '../../components/LocationAutocomplete';
import Swal from 'sweetalert2';
import { API_BASE } from '../../services/apiClient';
import { normalizeFavorites } from '../../utils/favorites';
import heroApartamento from '../../assets/tipos/apartamento.png';
import heroCastelo from '../../assets/tipos/castelo.png';
import heroCondominio from '../../assets/tipos/condominio.png';
import heroHotel from '../../assets/tipos/praiaPortugal.png';
import heroCasa from '../../assets/tipos/casa.png';
import heroStudio from '../../assets/tipos/studio.png';

const HERO_CATEGORY_TYPES = [
  {
    name: 'Apartamento',
    image: heroApartamento,
    slugCATEGORIA: 'apartamento',
    codigoCATEGORIA: 28
  },
  {
    name: 'Castelo',
    image: heroCastelo,
    slugCATEGORIA: 'castelo',
    codigoCATEGORIA: 2
  },
  {
    name: 'Condomínio',
    image: heroCondominio,
    slugCATEGORIA: 'condominio',
    codigoCATEGORIA: 6
  },
  {
    name: 'Hotel',
    image: heroHotel,
    slugCATEGORIA: 'hotel',
    codigoCATEGORIA: 5
  },
  {
    name: 'Casa',
    image: heroCasa,
    slugCATEGORIA: 'casa',
    codigoCATEGORIA: 11
  },
  {
    name: 'Studio',
    image: heroStudio,
    slugCATEGORIA: 'studio',
    codigoCATEGORIA: 20
  }
];

const HeroSection = styled.div`
  background: ${({ $bgimage, $categorySlug }) => {
    // Categoria especial para imóveis de luxo: imagem de luxo com overlay
    if (String($categorySlug).toLowerCase() === 'imoveis-de-luxo') {
      return "linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.35)), url('/yg8BLe3RARZty1cXa7yA5oL1VmLFx5tqKR0ivmNC.jpg') center/cover no-repeat";
    }
    if ($bgimage) {
      return `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.35)), url('${$bgimage}') center/cover no-repeat`;
    }
    return '#ba824f';
  }};
  text-align: center;
  padding: 100px 20px;
  margin-bottom: 40px;
  position: relative;

  h1 {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 20px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
    color: white;
  }

  p {
    font-size: 1.5rem;
    font-weight: 400;
    max-width: 800px;
    margin: 0 auto;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
    color: white;
  }

  @media (max-width: 768px) {
    padding: 60px 20px;

    h1 {
      font-size: 2rem;
    }

    p {
      font-size: 1.125rem;
    }
  }
`;

const Pagination = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
  padding: 16px 0 40px;

  button {
    padding: 8px 16px;
    border-radius: 8px;
    border: 2px solid #eee;
    background: white;
    color: #333;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:enabled {
      background: #f5f5f5;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  span {
    color: #555;
    font-weight: 500;
  }
`;

const SearchBar = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 40px;
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const TabGroup = styled.div`
  display: flex;
  gap: 8px;
  padding: 4px;
  background: #f5f5f5;
  border-radius: 8px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }

  button {
    padding: 8px 16px;
    border: none;
    border-radius: 6px;
    background: none;
    color: #666;
    cursor: pointer;
    transition: all 0.3s ease;

    @media (max-width: 768px) {
      flex: 1 1 0;
      text-align: center;
    }

    &.active {
      background: #34574A;
      color: white;
    }
  }
`;

const SearchInput = styled.div`
  flex: 1;
  position: relative;
  min-width: 200px;

  input {
    width: 100%;
    padding: 12px;
    padding-left: 40px;
    border: 2px solid #eee;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.3s ease;

    @media (max-width: 768px) {
      width: 100%;
    }

    &:focus {
      border-color: #34574A;
      outline: none;
    }
  }

  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
  }
`;

const PropertyType = styled.div`
  position: relative;
  min-width: 200px;

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
  }

  .select-header {
    width: 100%;
    padding: 12px;
    border: 2px solid #eee;
    border-radius: 8px;
    font-size: 1rem;
    background: white;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #333;

    &:hover {
      border-color: #34574A;
    }
  }

  .select-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    width: 100%;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    opacity: ${props => props.$isOpen ? 1 : 0};
    visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
    transform: translateY(${props => props.$isOpen ? '0' : '-10px'});
    transition: all 0.3s ease;
    max-height: 260px;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .select-option {
    padding: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #333;

    &:hover {
      background: #f5f5f5;
    }

    &:first-child {
      border-radius: 8px 8px 0 0;
    }

    &:last-child {
      border-radius: 0 0 8px 8px;
    }
  }
`;

const PriceRange = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 300px;

   @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    min-width: 0;
  }

  select {
    padding: 12px;
    border: 2px solid #eee;
    border-radius: 8px;
    font-size: 1rem;
    appearance: none;
    background: white;
    cursor: pointer;
    transition: all 0.3s ease;

    @media (max-width: 768px) {
      width: 100%;
    }

    &:focus {
      border-color: #34574A;
      outline: none;
    }
  }

  input {
    width: 120px;
    padding: 12px;
    border: 2px solid #eee;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.3s ease;

    &:focus {
      border-color: #34574A;
      outline: none;
    }
  }
`;

const FilterButton = styled.button`
  padding: 12px 24px;
  background: white;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 1rem;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #f5f5f5;
  }
`;

const SearchButton = styled.button`
  padding: 12px 24px;
  background: #34574A;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #2a4539;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 32px;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;

  h2 {
    font-size: 1.5rem;
    margin-bottom: 24px;
    color: #333;
  }
`;

const FilterSection = styled.div`
  margin-bottom: 32px;

  h3 {
    font-size: 1.125rem;
    margin-bottom: 16px;
    color: #333;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;

  button {
    padding: 8px 16px;
    border: 2px solid #eee;
    border-radius: 6px;
    background: white;
    color: #666;
    cursor: pointer;
    transition: all 0.3s ease;

    &.active {
      background: #34574A;
      color: white;
      border-color: #34574A;
    }

    &:hover:not(.active) {
      background: #f5f5f5;
    }
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 32px;

  button {
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;

    &.cancel {
      background: white;
      border: 2px solid #eee;
      color: #666;

      &:hover {
        background: #f5f5f5;
      }
    }

    &.apply {
      background: #34574A;
      border: none;
      color: white;

      &:hover {
        background: #2a4539;
      }
    }
  }
`;

const Category = () => {
  console.log('🔍 [Category] Componente Category renderizado');
  const { t, i18n } = useTranslation();
  const { category } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn, user } = useAuth();
  const globalState = useGlobalState();
  const handleOpenLogin = globalState?.handleOpenLogin || (() => {});
  const { currency } = useCurrency();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('');
  const [isPropertyTypeOpen, setIsPropertyTypeOpen] = useState(false);
  const propertyTypeRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const [heroImage, setHeroImage] = useState('');

  const [filters, setFilters] = useState({
    location: '',
    propertyType: 'all',
    minPrice: '',
    maxPrice: '',
    currency: 'EUR',
    bedrooms: '',
    bathrooms: '',
    parkingSpaces: '',
    minArea: '',
    maxArea: '',
    amenities: []
  });
  const [categories, setCategories] = useState([]);
  // FAVORITOS
  const [favorites, setFavorites] = useState([]);
  const [locationError, setLocationError] = useState('');
  const [error, setError] = useState('');
  // Paginação e ordenação
  const [page, setPage] = useState(1);
  const [pageSize] = useState(6);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [orderBy, setOrderBy] = useState('datacriacao');
  const [orderDirection] = useState('DESC');

  // Reseta para a primeira página ao alterar aba, filtros ou ordenação
  useEffect(() => {
    setPage(1);
  }, [activeTab, filters, orderBy]);

  // Ver fotos (lightbox simples com SweetAlert2)
  const handleViewPhotos = (property) => {
    try {
      // Normaliza array de imagens em URLs
      let raw = [];
      if (Array.isArray(property.fotos) && property.fotos.length) raw = property.fotos;
      else if (Array.isArray(property.imagens) && property.imagens.length) raw = property.imagens;
      const imgs = raw
        .map(x => (typeof x === 'string' ? x : (x?.urlFOTO || x?.pathFOTO || '')))
        .filter(Boolean);
      if (!imgs.length) return;
      let idx = 0;
      const renderHtml = (src, index, total) => `
        <div style="position:relative; display:flex; align-items:center; justify-content:center; background:#111; padding:0; margin:0;">
          <button id="lb-prev" style="position:absolute; left:12px; top:50%; transform:translateY(-50%); z-index:10; border:none; background:#fff; width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; cursor:pointer;">❮</button>
          <img id="lb-img" src="${src}" style="max-width:90vw; max-height:80vh; object-fit:contain; border-radius:4px;" />
          <button id="lb-next" style="position:absolute; right:12px; top:50%; transform:translateY(-50%); z-index:10; border:none; background:#fff; width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; cursor:pointer;">❯</button>
          <div id="lb-counter" style="position:absolute; bottom:12px; left:50%; transform:translateX(-50%); background:rgba(0,0,0,.6); color:#fff; padding:6px 10px; border-radius:12px; font-weight:600;">${index+1} / ${total}</div>
        </div>`;
      const update = (swalContent) => {
        const img = swalContent.querySelector('#lb-img');
        const counter = swalContent.querySelector('#lb-counter');
        if (img) img.src = imgs[idx];
        if (counter) counter.textContent = `${idx + 1} / ${imgs.length}`;
      };
      Swal.fire({
        html: renderHtml(imgs[idx], idx, imgs.length),
        width: '90%', padding: 0, background: '#111',
        showConfirmButton: false, showCloseButton: true, allowOutsideClick: false,
        didOpen: (el) => {
          const prev = el.querySelector('#lb-prev');
          const next = el.querySelector('#lb-next');
          const onPrev = () => { idx = (idx - 1 + imgs.length) % imgs.length; update(el); };
          const onNext = () => { idx = (idx + 1) % imgs.length; update(el); };
          prev?.addEventListener('click', onPrev);
          next?.addEventListener('click', onNext);
        }
      });
    } catch (_) {
      // Silently ignore photo viewing errors
    }
  };

  // Ver vídeo (aceita iframe embed ou URL e converte para embed)
  const handleViewVideo = (property) => {
    let raw = (property?.videoIMOVEL || '').trim();
    if (!raw) {
      try { Swal.fire({ toast: true, position: 'top-end', timer: 2000, showConfirmButton: false, icon: 'info', title: 'Imóvel sem vídeo' }); } catch {
        // Silently ignore SweetAlert errors
      }
      return;
    }

    // Se já é um iframe, usa como está
    const hasIframe = /<\s*iframe[\s\S]*><\s*\/\s*iframe\s*>/i.test(raw) || /<\s*iframe[^>]*>/i.test(raw);

    // Converte URLs do YouTube/Vimeo para embed
    const toEmbed = (url) => {
      try {
        const u = new URL(url);
        const host = u.hostname.replace(/^www\./, '');
        if (host.includes('youtube.com')) {
          // formatos: /watch?v=ID, /shorts/ID, /embed/ID
          const v = u.searchParams.get('v');
          let id = v;
          if (!id && u.pathname.startsWith('/shorts/')) id = u.pathname.split('/shorts/')[1]?.split(/[?&#]/)[0];
          if (!id && u.pathname.startsWith('/embed/')) id = u.pathname.split('/embed/')[1]?.split(/[?&#]/)[0];
          if (!id && u.pathname.startsWith('/watch')) id = v;
          if (id) return `https://www.youtube.com/embed/${id}`;
          return url;
        }
        if (host.includes('youtu.be')) {
          const id = u.pathname.slice(1).split(/[?&#]/)[0];
          if (id) return `https://www.youtube.com/embed/${id}`;
          return url;
        }
        if (host.includes('vimeo.com')) {
          const id = u.pathname.replace(/\/(video|channels|ondemand)/, '').split('/').filter(Boolean)[0];
          if (id) return `https://player.vimeo.com/video/${id}`;
          return url;
        }
      } catch {
        // Silently ignore URL parsing errors
      }
      return url;
    };

    let contentHtml = raw;
    if (!hasIframe) {
      const embedSrc = toEmbed(raw);
      contentHtml = `
        <div style="position:relative;padding-top:56.25%;width:100%;">
          <iframe src="${embedSrc}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" allowfullscreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"></iframe>
        </div>`;
    }

    Swal.fire({
      title: 'Vídeo do imóvel',
      html: `<div style="padding:0;position:relative;">${contentHtml}</div>`,
      width: '80%', padding: 0, background: '#000', showConfirmButton: false, showCloseButton: true
    });
  };

  // Comparar (adiciona ao localStorage e opcionalmente navega)
  const handleCompare = (property) => {
    const absolutize = (src) => {
      if (!src || typeof src !== 'string') return '';
      if (src.startsWith('http') || src.startsWith('data:')) return src;
      const cleaned = src.replace(/^\.*\/+/, '').replace(/^images\//, 'images/');
      const host = (import.meta.env.VITE_PUBLIC_ASSETS_HOST || '').replace(/\/$/, '');
      const base = host || String(import.meta.env.VITE_URL_API || '').replace(/\/api\/?$/, '');
      return `${base}/${cleaned}`;
    };
    const firstPhotoRaw = Array.isArray(property.fotos) && property.fotos.length
      ? (typeof property.fotos[0] === 'string' ? property.fotos[0] : (property.fotos[0]?.urlFOTO || property.fotos[0]?.pathFOTO || ''))
      : '';
    const mapped = {
      id: property.codigoIMOVEL || property.id,
      title: property.tituloIMOVEL || 'Sem título',
      price: property.valorIMOVEL ?? '',
      location: property.enderecoIMOVEL || '',
      bedrooms: property.quartosIMOVEL ?? '',
      bathrooms: property.banheirosIMOVEL ?? '',
      area: property.metragemIMOVEL ?? '',
      garage: property.vagasgaragemIMOVEL ?? '',
      image: absolutize(firstPhotoRaw)
    };
    try {
      const prev = JSON.parse(localStorage.getItem('compare_items') || '[]');
      const exists = prev.some(p => String(p.id) === String(mapped.id));
      const next = exists ? prev : [...prev, mapped];
      localStorage.setItem('compare_items', JSON.stringify(next));
      Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false, icon: 'success', title: 'Adicionado à comparação' });
      if (next.length >= 2) {
        // Sugere ir comparar quando já tem 2 ou mais
        setTimeout(() => navigate('/compare', { state: { items: next } }), 250);
      }
    } catch (_) {
      // Silently ignore localStorage errors
    }
  };

  // Carrega favoritos do usuário logado
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!isLoggedIn || !user) return;
      const token = localStorage.getItem('auth_token');
      try {
        const res = await fetch(`${import.meta.env.VITE_URL_API}/favoritos/usuario/${user.codigo || user.id || user.userId}`, {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await res.json();
        setFavorites(normalizeFavorites(data));
      } catch {
        setFavorites([]);
      }
    };
    fetchFavorites();
  }, [isLoggedIn, user]);

  // Função para favoritar
  const handleFavorite = async (property) => {
    if (!isLoggedIn) {
      handleOpenLogin();
      return;
    }
    try {
      const token = localStorage.getItem('auth_token');
      const payload = {
        imovelFAVORITO: property.codigoIMOVEL || property.id,
        usuarioFAVORITO: user?.codigo || user?.id || user?.userId,
        ativoFAVORITO: 1
      };
      const response = await fetch(`${import.meta.env.VITE_URL_API}/favoritos`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        // Atualize favoritos após favoritar
        const res = await fetch(`${import.meta.env.VITE_URL_API}/favoritos/usuario/${payload.usuarioFAVORITO}`, {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await res.json();
        setFavorites(normalizeFavorites(data));
        window.dispatchEvent(new Event('favoritesChanged'));
        try { Swal.fire({ toast: true, position: 'top-end', timer: 2000, showConfirmButton: false, icon: 'success', title: 'Adicionado aos favoritos' }); } catch {
          // Silently ignore SweetAlert errors
        }
      } else {
        try { Swal.fire({ toast: true, position: 'top-end', timer: 2500, showConfirmButton: false, icon: 'error', title: 'Erro ao favoritar' }); } catch {
          // Silently ignore SweetAlert errors
        }
      }
    } catch (err) {
      try { Swal.fire({ toast: true, position: 'top-end', timer: 2500, showConfirmButton: false, icon: 'error', title: 'Erro ao favoritar' }); } catch {
        // Silently ignore SweetAlert errors
      }
    }
  };

  // Função para desfavoritar
  const handleUnfavorite = async (property) => {
    if (!isLoggedIn) {
      handleOpenLogin();
      return;
    }
    try {
      const token = localStorage.getItem('auth_token');
      // Descubra o codigoFAVORITO pelo imovel
      const fav = favorites.find(f => String(f.imovelFAVORITO) === String(property.codigoIMOVEL || property.id));
      if (!fav) return;
      await fetch(`${import.meta.env.VITE_URL_API}/favoritos/${fav.codigoFAVORITO}`, {
        method: 'DELETE',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${token}`
        }
      });
      // Atualize favoritos após remover
      const res = await fetch(`${import.meta.env.VITE_URL_API}/favoritos/usuario/${user.codigo || user.id || user.userId}`, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      setFavorites(normalizeFavorites(data));
      window.dispatchEvent(new Event('favoritesChanged'));
      try { Swal.fire({ toast: true, position: 'top-end', timer: 2000, showConfirmButton: false, icon: 'info', title: 'Removido dos favoritos' }); } catch {
        // Silently ignore SweetAlert errors
      }
    } catch (err) {
      try { Swal.fire({ toast: true, position: 'top-end', timer: 2500, showConfirmButton: false, icon: 'error', title: 'Erro ao remover favorito' }); } catch {
        // Silently ignore SweetAlert errors
      }
    }
  };

  // Helper: retorna true se o imóvel está favoritado
  const isFavorited = (propertyId) => {
    return favorites.some(fav => String(fav.imovelFAVORITO) === String(propertyId));
  };


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (propertyTypeRef.current && !propertyTypeRef.current.contains(event.target)) {
        setIsPropertyTypeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Fetch categories
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_URL_API}/categorias`);
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchCategories();
  }, []);

  // Resolve URL absoluta para imagens da API
  const resolveImageUrl = (src) => {
    if (!src || typeof src !== 'string') return '';
    if (src.startsWith('http') || src.startsWith('data:')) return src;
    const base = (API_BASE || '').replace(/\/api\/?$/, '');
    const path = src.startsWith('/') ? src : `/${src}`;
    return `${base}${path}`;
  };

  // Atualiza a imagem do hero com base na categoria vinda da API (imagemCATEGORIA)
  useEffect(() => {
    // Tenta a imagem da API; se não houver dados ou falhar, cai para fallback estático
    const found = (categories || []).find(cat => String(cat.codigoCATEGORIA) === String(category) || String(cat.slugCATEGORIA) === String(category));
    const apiImg = resolveImageUrl(found?.imagemCATEGORIA);
    if (apiImg) {
      setHeroImage(apiImg);
      return;
    }
    const heroType = HERO_CATEGORY_TYPES.find(t => String(t.codigoCATEGORIA) === String(category) || String(t.slugCATEGORIA) === String(category));
    if (heroType?.image) {
      setHeroImage(heroType.image);
    }
  }, [categories, category]);

  // Ao trocar de idioma, revalida o label do "Todos os tipos" forçando valor padrão
  useEffect(() => {
    console.log('🔍 [Category] useEffect de idioma executado');
    setFilters(prev => ({ ...prev, propertyType: 'all' }));
  }, [i18n.language]);

  console.log('🔍 [Category] Antes do useEffect principal - activeTab:', activeTab);

  // Busca imóveis - useEffect principal
  useEffect(() => {
    console.log('🔍 [Category] useEffect principal iniciado - activeTab:', activeTab);
    
    const fetchProperties = async () => {
      console.log('🔍 [Category] Iniciando busca de imóveis - activeTab:', activeTab);
      setLoading(true);
      setError('');
      try {
        const categoriaId = category;
        console.log('categoryId da rota:', categoriaId);
        console.log('slug da rota:', category);

        // A rota traz um código fixo (ex.: 28 = Apartamentos/Comprar), mas o mesmo nome
        // de categoria existe uma vez por modalidade (tipoCATEGORIA 1/2/3). Sem aba
        // selecionada consideramos todas as homônimas; com aba ativa, apenas a da modalidade.
        const tabTypeCode =
          activeTab === 'comprar' ? 1 :
          activeTab === 'arrendar' ? 2 :
          activeTab === 'ferias' ? 3 :
          null;
        const normalizeKey = (s) => String(s || '')
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase()
          .trim();
        const categoryList = Array.isArray(categories) ? categories : [];
        const currentCategory = [...categoryList, ...HERO_CATEGORY_TYPES].find(cat =>
          String(cat.codigoCATEGORIA) === String(categoriaId) ||
          String(cat.slugCATEGORIA) === String(categoriaId)
        );
        const resolvedCategoryId = currentCategory
          ? currentCategory.codigoCATEGORIA
          : (/^\d+$/.test(String(categoriaId)) ? Number(categoriaId) : categoriaId);

        const siblings = currentCategory
          ? categoryList.filter(cat =>
              normalizeKey(cat.nomeCATEGORIA) === normalizeKey(currentCategory.nomeCATEGORIA || currentCategory.name)
            )
          : [];
        const scoped = tabTypeCode === null
          ? siblings
          : siblings.filter(cat => Number(cat.tipoCATEGORIA) === Number(tabTypeCode));
        const categoriaParam = scoped.length
          ? scoped.map(cat => cat.codigoCATEGORIA).join(',')
          : String(resolvedCategoryId);
        console.log('🔍 [Category] categorias resolvidas:', { activeTab, tabTypeCode, categoriaParam });

        const params = new URLSearchParams();
        // Busca todos os imóveis filtrados; a paginação (6 por página) é feita no frontend
        // Envia o(s) ID(s) de categoria para o backend filtrar por categoriaIMOVEL
        params.set('categoria', categoriaParam);
        if (filters.location) params.set('city', String(filters.location));
        // Filtro do dropdown: o valor é um codigoCATEGORIA, então sobrescreve a categoria
        // da rota. Aplica apenas quando não for "Todos os tipos".
        if (filters.propertyType && filters.propertyType !== 'all') {
          params.set('categoria', String(filters.propertyType));
        }
        if (orderBy) params.set('orderBy', orderBy);
        if (orderDirection) params.set('orderDirection', orderDirection);
        console.log('tipo selecionado:', activeTab);
        console.log('params enviados:', Object.fromEntries(params));

        // Tabs Comprar/Arrendar/Férias -> filtros de operação no servidor
        // Sempre aplica o filtro de tipo para garantir que a aba selecionada
        // corresponda ao tipoIMOVEL, independentemente de haver categorias irmãs.
        if (activeTab === 'comprar') {
          params.set('tipo', '1'); // venda
          console.log('🔍 [Category] Filtro aplicado: tipo=1 (Comprar)');
        } else if (activeTab === 'arrendar') {
          params.set('tipo', '2'); // aluguel
          console.log('🔍 [Category] Filtro aplicado: tipo=2 (Arrendar)');
        } else if (activeTab === 'ferias') {
          params.set('tipo', '3'); // férias
          params.set('labelvalor', 'ferias');
          console.log('🔍 [Category] Filtro aplicado: tipo=3, labelvalor=ferias (Férias)');
        }

        console.log('Categoria enviada para API:', Object.fromEntries(params));
        const url = `${import.meta.env.VITE_URL_API}/imoveis?${params.toString()}`;
        console.log('🔍 [Category] URL da requisição:', url);
        console.log('🔍 [Category] Parâmetros:', Object.fromEntries(params));
        const response = await fetch(url);
        if (!response.ok) throw new Error('Erro ao buscar imóvel');
        const payload = await response.json();
        console.log('Resposta da API da categoria:', payload);
        console.log('🔍 [Category] Resposta da API:', payload);
        console.log('🔍 [Category] Número de imóveis retornados:', Array.isArray(payload?.data) ? payload.data.length : 0);
        console.log('resposta da API:', payload);

        const listRaw = Array.isArray(payload?.data) ? payload.data : [];

        // Remove itens excluídos/inativos antes de qualquer mapeamento
        const sanitized = listRaw.filter(item => {
          const isExcludedFlag = Number(item?.excluidoIMOVEL) === 1 || Number(item?.deletadoIMOVEL) === 1;
          const isDeletedAt = !!item?.deleted_at || !!item?.deletedAt;
          const isInactive = Number(item?.ativoIMOVEL) === -1 || Number(item?.ativoIMOVEL) === 0;
          return !(isExcludedFlag || isDeletedAt || isInactive);
        });

        // Log da categoria salva em cada imóvel para depuração
        sanitized.forEach(imovel => {
          console.log('Categoria salva no imóvel:', {
            codigo: imovel.codigoIMOVEL,
            titulo: imovel.tituloIMOVEL,
            categoriaIMOVEL: imovel.categoriaIMOVEL,
            tipoIMOVEL: imovel.tipoIMOVEL
          });
        });

        // Usa diretamente os dados do backend sem filtro adicional de tipo no frontend
        // O backend já aplica o filtro de tipo via parâmetro 'tipo'
        const list = sanitized;
        const total = list.length;
        const totalPages = Math.max(1, Math.ceil(total / pageSize));
        const paginated = list.slice((page - 1) * pageSize, page * pageSize);
        console.log('lista final renderizada:', paginated);
        setProperties(paginated);
        setTotalPages(totalPages);
        setTotal(total);
      } catch (error) {
        console.error('Erro ao carregar dados do imóvel:', error);
        setError('Não foi possível carregar os imóveis.');
        setProperties([]);
        setTotalPages(1);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };

    console.log('🔍 [Category] Chamando fetchProperties');
    fetchProperties();
  }, [
    category,
    categories,
    page,
    pageSize,
    orderBy,
    orderDirection,
    filters.location,
    filters.propertyType,
    activeTab,
  ]);

  const handleSearch = () => {
    // Não exigir localização; apenas reinicia para a página 1 com filtros atuais
    if (filters.location && String(filters.location).trim().length < 2) {
      setLocationError('Por favor, insira uma localização válida.');
      return;
    }
    setLocationError('');
    setPage(1);
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Dropdown "Todos os tipos" nesta página de categoria deve se comportar
  // como o HeroSearch: opções baseadas nas categorias filtradas por tipoCATEGORIA
  // conforme a aba ativa (comprar, arrendar, férias).
  const typeCode =
    activeTab === 'comprar' ? 1 :
    activeTab === 'arrendar' ? 2 :
    activeTab === 'ferias' ? 3 :
    null;

  const filteredCategories = categories.filter(cat =>
    typeCode === null || Number(cat.tipoCATEGORIA) === Number(typeCode)
  );

  const propertyTypes = [
    { value: 'all', label: t('hero.search.propertyType') },
    ...filteredCategories.map(cat => ({
      value: cat.codigoCATEGORIA,
      label: translateCategoryLabel(cat),
    })),
  ];

  function translateCategoryLabel(cat = {}) {
    const slug = String(cat.slugCATEGORIA || '').toLowerCase();
    const rawName = String(cat.nomeCATEGORIA || '').toLowerCase();
    const norm = (s) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/\s+/g, '-').replace(/_/g, '-');

    const slugNorm = norm(slug);
    const nameNorm = norm(rawName);

    const slugKey = `category.types.${slugNorm}`;
    const nameKey = `category.types.${nameNorm}`;

    const fromSlug = t(slugKey, { defaultValue: slugKey });
    if (fromSlug !== slugKey) return fromSlug;

    const fromName = t(nameKey, { defaultValue: nameKey });
    if (fromName !== nameKey) return fromName;

    // Dicionário manual para idiomas comuns (fr/en/es)
    const lang = (i18n.language || '').split('-')[0];
    const dict = {
      fr: {
        'apart-hotel': 'Appart-hôtel',
        'apartamento': 'Appartement',
        'apartamentos': 'Appartements',
        'armazens': 'Entrepôts',
        'condominios': 'Copropriétés',
        'hotel': 'Hôtel',
        'hoteis': 'Hôtels',
        'moradias': 'Maisons',
        'palacios-castelos': 'Palais / Châteaux',
        'predios': 'Immeubles',
        'predios-comerciais-coworking': 'Immeubles commerciaux / Coworking',
        'salas-coworking': 'Salles / Coworking',
        'garagens': 'Garages',
        'lojas': 'Magasins',
        'quintas-e-herdades': 'Domaines / Fermes',
        'terrenos': 'Terrains',
      },
      en: {
        'apart-hotel': 'Aparthotel',
        'apartamento': 'Apartment',
        'apartamentos': 'Apartments',
        'armazens': 'Warehouses',
        'condominios': 'Condominiums',
        'hotel': 'Hotel',
        'hoteis': 'Hotels',
        'moradias': 'Houses',
        'palacios-castelos': 'Palaces / Castles',
        'predios': 'Buildings',
        'predios-comerciais-coworking': 'Commercial buildings / Coworking',
        'salas-coworking': 'Offices / Coworking',
        'garagens': 'Garages',
        'lojas': 'Stores',
        'quintas-e-herdades': 'Farms / Estates',
        'terrenos': 'Land',
      },
      es: {
        'apart-hotel': 'Apart-hotel',
        'apartamento': 'Apartamento',
        'apartamentos': 'Apartamentos',
        'armazens': 'Almacenes',
        'condominios': 'Condominios',
        'hotel': 'Hotel',
        'hoteis': 'Hoteles',
        'moradias': 'Casas',
        'palacios-castelos': 'Palacios / Castillos',
        'predios': 'Edificios',
        'predios-comerciais-coworking': 'Edificios comerciales / Coworking',
        'salas-coworking': 'Oficinas / Coworking',
        'garagens': 'Garajes',
        'lojas': 'Tiendas',
        'quintas-e-herdades': 'Fincas / Haciendas',
        'terrenos': 'Terrenos',
      },
    };

    const dictVal = dict[lang]?.[slugNorm] || dict[lang]?.[nameNorm];
    if (dictVal) return dictVal;

    return cat.nomeCATEGORIA || cat.slugCATEGORIA || rawName;
  }

  const displayCategoryName = (() => {
    const found = categories.find(cat => String(cat.codigoCATEGORIA) === String(category) || String(cat.slugCATEGORIA) === String(category));

    // Usa rótulo traduzido quando disponível (para evitar ficar apenas "Categoria" em outros idiomas)
    const translated = translateCategoryLabel(
      found || { nomeCATEGORIA: category, slugCATEGORIA: category }
    );
    const translatedClean = String(translated || '').trim();
    const isNumericCategory = /^\d+$/.test(String(category));
    // Se a "tradução" for apenas o número (ex.: category=20), ignora e tenta mapeamentos.
    if (translatedClean && !(isNumericCategory && translatedClean === String(category))) {
      return translatedClean;
    }

    // Fallback: tenta usar mapeamento local de tipos do Hero (codes usados no banner)
    const heroType = HERO_CATEGORY_TYPES.find(t => String(t.codigoCATEGORIA) === String(category) || String(t.slugCATEGORIA) === String(category));
    if (heroType) {
      const fromHero = translateCategoryLabel({ nomeCATEGORIA: heroType.name, slugCATEGORIA: heroType.slugCATEGORIA });
      const heroClean = String(fromHero || '').trim();
      if (heroClean) return heroClean;
    }

    // Fallback para o nome bruto capitalizado
    const raw = found?.nomeCATEGORIA || found?.slugCATEGORIA || category;
    const cleaned = String(raw || '')
      .replace(/^[0-9]+[-_\s]*/, '')
      .replace(/[-_]+/g, ' ')
      .trim();
    if (!cleaned) return 'Categoria';
    const finalName = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    console.log('[Category] Debug displayCategoryName:', { raw, cleaned, finalName, category });
    return finalName;
  })();

  // Verifica se o nome já contém "Imóveis" para evitar duplicação no título
  const heroTitle = React.useMemo(() => {
    const categoryName = displayCategoryName;
    if (categoryName.toLowerCase().includes('imóveis') || categoryName.toLowerCase().includes('imoveis')) {
      return categoryName; // Se já tem "Imóveis", usa o nome direto
    }
    return `Imóveis Na Categoria: ${categoryName}`; // Senão, adiciona o prefixo
  }, [displayCategoryName]);

  return (
    <>
      <HeroSection $categorySlug={category} $bgimage={heroImage}>
        <h1>{heroTitle}</h1>
        <p>{t('category.hero.subtitle', { defaultValue: 'Explore os melhores imóveis disponíveis nesta categoria' })}</p>
      </HeroSection>

      <CategoryPage>
        <SearchBar>
          <TabGroup>
            <button 
              className={activeTab === 'comprar' ? 'active' : ''} 
              onClick={() => {
                console.log('🔍 [Category] Botão Comprar clicado');
                setActiveTab('comprar');
              }}
            >
              {t('category.tabs.buy', { defaultValue: 'Comprar' })}
            </button>
            <button 
              className={activeTab === 'arrendar' ? 'active' : ''} 
              onClick={() => {
                console.log('🔍 [Category] Botão Arrendar clicado');
                setActiveTab('arrendar');
              }}
            >
              {t('category.tabs.rent', { defaultValue: 'Arrendar' })}
            </button>
            <button 
              className={activeTab === 'ferias' ? 'active' : ''} 
              onClick={() => {
                console.log('🔍 [Category] Botão Férias clicado');
                setActiveTab('ferias');
              }}
            >
              {t('category.tabs.vacation', { defaultValue: 'Férias' })}
            </button>
          </TabGroup>

          <SearchInput>
            <LocationAutocomplete
              value={filters.location}
              onChange={(value) => handleFilterChange('location', value)}
              error={locationError}
              placeholder={t('searchResults.search.locationPlaceholder')}
            />
          </SearchInput>

          <PropertyType ref={propertyTypeRef} $isOpen={isPropertyTypeOpen}>
            <div 
              className="select-header"
              onClick={() => setIsPropertyTypeOpen(!isPropertyTypeOpen)}
            >
              <span>{propertyTypes.find(type => type.value === filters.propertyType)?.label || t('hero.search.propertyType')}</span>
              <svg 
                width="12" 
                height="8" 
                viewBox="0 0 12 8" 
                fill="none" 
                style={{ transform: isPropertyTypeOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.3s ease' }}
              >
                <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <div
              className="select-dropdown"
              ref={dropdownRef}
              onWheel={(e) => {
                // Evita que a rolagem do dropdown desça a página
                e.stopPropagation();
                const el = dropdownRef.current;
                if (!el) return;
                const atTop = el.scrollTop <= 0 && e.deltaY < 0;
                const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1 && e.deltaY > 0;
                if (atBottom) {
                  // Fecha automaticamente ao chegar ao fim
                  setIsPropertyTypeOpen(false);
                }
                if (atTop || atBottom) {
                  e.preventDefault();
                }
              }}
              onScroll={(e) => {
                const el = e.currentTarget;
                // Fecha automaticamente quando o usuário alcança o final via scroll da barra
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - 1) {
                  setIsPropertyTypeOpen(false);
                }
              }}
            >
              {propertyTypes.map(type => (
                <div 
                  key={type.value}
                  className="select-option"
                  onClick={() => {
                    handleFilterChange('propertyType', type.value);
                    setIsPropertyTypeOpen(false);
                  }}
                >
                  {type.label}
                </div>
              ))}
            </div>
          </PropertyType>

          <PriceRange>
            {/* Moeda global (somente exibição) */}
            <CurrencyBadge title="Moeda selecionada no cabeçalho">
              {currency}
            </CurrencyBadge>
            <input 
              type="number" 
              placeholder={t('hero.search.minPrice')} 
              value={filters.minPrice}
              onChange={(e) => handleFilterChange('minPrice', e.target.value)}
            />
            <span>{t('common.to')}</span>
            <input 
              type="number" 
              placeholder={t('hero.search.maxPrice')} 
              value={filters.maxPrice}
              onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
            />
          </PriceRange>

          {/* Ordenação compacta + busca */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <select
              value={orderBy}
              onChange={(e) => { setOrderBy(e.target.value); setPage(1); }}
              style={{ padding: '10px 12px', border: '2px solid #eee', borderRadius: 8, minWidth: 140 }}
            >
              <option value="datacriacao">Mais recentes</option>
              <option value="valor">Preço</option>
              <option value="city">Cidade</option>
            </select>
            <SearchButton onClick={handleSearch}>
              Pesquisar
            </SearchButton>
          </div>
        </SearchBar>

      {loading ? (
        <NoResults>
          <h3>Carregando...</h3>
          <p>Buscando as melhores opções para você</p>
        </NoResults>
      ) : error ? (
        <NoResults>
          <h3>Erro</h3>
          <p>{error}</p>
        </NoResults>
      ) : properties.length > 0 ? (
        <PropertiesGrid>
          {properties.map((property) => (
            <PropertyCard
              key={property.codigoIMOVEL || property.id}
              property={property}
              isFavorited={isFavorited(property.codigoIMOVEL || property.id)}
              onFavorite={handleFavorite}
              onUnfavorite={handleUnfavorite}
              onViewPhotos={handleViewPhotos}
              onViewVideo={handleViewVideo}
              onCompare={handleCompare}
            />
          ))}
        </PropertiesGrid>
      ) : (
        <NoResults>
          <h3>Ops! Nesta região ainda não temos imóveis...mas em breve teremos novidades.</h3>
          <p></p>
        </NoResults>
      )}
      {/* Paginação */}
      {!loading && !error && total > 0 && (
        <Pagination>
          <button
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            Anterior
          </button>
          <span>
            Página {page} de {totalPages} • {total} resultados
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          >
            Próxima
          </button>
        </Pagination>
      )}
    </CategoryPage>

    <Modal $isOpen={isModalOpen}>
      <ModalContent>
        <h2>Advanced Filters</h2>

        <FilterSection>
          <h3>Bedrooms</h3>
          <ButtonGroup>
            <button className={filters.bedrooms === '1+' ? 'active' : ''} onClick={() => handleFilterChange('bedrooms', '1+')}>1+</button>
            <button className={filters.bedrooms === '2+' ? 'active' : ''} onClick={() => handleFilterChange('bedrooms', '2+')}>2+</button>
            <button className={filters.bedrooms === '3+' ? 'active' : ''} onClick={() => handleFilterChange('bedrooms', '3+')}>3+</button>
            <button className={filters.bedrooms === '4+' ? 'active' : ''} onClick={() => handleFilterChange('bedrooms', '4+')}>4+</button>
          </ButtonGroup>
        </FilterSection>

        <FilterSection>
          <h3>Bathrooms</h3>
          <ButtonGroup>
            <button className={filters.bathrooms === '1+' ? 'active' : ''} onClick={() => handleFilterChange('bathrooms', '1+')}>1+</button>
            <button className={filters.bathrooms === '2+' ? 'active' : ''} onClick={() => handleFilterChange('bathrooms', '2+')}>2+</button>
            <button className={filters.bathrooms === '3+' ? 'active' : ''} onClick={() => handleFilterChange('bathrooms', '3+')}>3+</button>
            <button className={filters.bathrooms === '4+' ? 'active' : ''} onClick={() => handleFilterChange('bathrooms', '4+')}>4+</button>
          </ButtonGroup>
        </FilterSection>

        <FilterSection>
          <h3>Parking spaces</h3>
          <ButtonGroup>
            <button className={filters.parkingSpaces === 'whatever' ? 'active' : ''} onClick={() => handleFilterChange('parkingSpaces', 'whatever')}>Whatever</button>
            <button className={filters.parkingSpaces === '1+' ? 'active' : ''} onClick={() => handleFilterChange('parkingSpaces', '1+')}>1+</button>
            <button className={filters.parkingSpaces === '2+' ? 'active' : ''} onClick={() => handleFilterChange('parkingSpaces', '2+')}>2+</button>
            <button className={filters.parkingSpaces === '3+' ? 'active' : ''} onClick={() => handleFilterChange('parkingSpaces', '3+')}>3+</button>
            <button className={filters.parkingSpaces === '4+' ? 'active' : ''} onClick={() => handleFilterChange('parkingSpaces', '4+')}>4+</button>
          </ButtonGroup>
        </FilterSection>

        <FilterSection>
          <h3>Area</h3>
          <PriceRange>
            <input 
              type="number" 
              placeholder="Min 20m²" 
              value={filters.minArea}
              onChange={(e) => handleFilterChange('minArea', e.target.value)}
            />
            <span>to</span>
            <input 
              type="number" 
              placeholder="Max 1.000m²" 
              value={filters.maxArea}
              onChange={(e) => handleFilterChange('maxArea', e.target.value)}
            />
          </PriceRange>
        </FilterSection>

        <ModalActions>
          <button className="cancel" onClick={() => setIsModalOpen(false)}>
            Cancel
          </button>
          <button className="apply" onClick={() => {
            handleSearch();
            setIsModalOpen(false);
          }}>
            Apply Filters
          </button>
        </ModalActions>
      </ModalContent>
    </Modal>
  </>
);
};

// Badge para exibir moeda global
const CurrencyBadge = styled.div`
  padding: 10px 14px;
  border: 2px solid #eee;
  border-radius: 8px;
  font-weight: 600;
  color: #333;
  min-width: 64px;
  text-align: center;
`;

const CategoryPage = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const PropertiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-bottom: 40px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;

  h3 {
    font-size: 1.5rem;
    margin-bottom: 16px;
    color: #333;
  }

  p {
    font-size: 1rem;
    max-width: 500px;
    margin: 0 auto;
  }
`;

export default Category;